import * as React from 'react';
import { Row, Col, Badge, Space } from 'antd';
import clsx from 'clsx';
import { NoContentArticle, Tag } from '../admin-knowledge-base.type';
import Controls from './controls';
import { getMenuItems } from '../admin-knowledge-base.utils';
import { PlusCircleOutlined } from '@ant-design/icons';
import { CSSProperties, memo } from 'react';
import Button from '/react4xp/mixins/ui/button/button';
import {
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    rectSortingStrategy,
    sortableKeyboardCoordinates,
    useSortable,
} from '@dnd-kit/sortable';
import { DragOutlined } from '@ant-design/icons';
import { CSS } from '@dnd-kit/utilities';
import TagIcon from '/react4xp/parts/knowledge-base-common/components/tag-icon';
import {validatePrivileges} from "mixins/helpers";

interface CategoryViewProps {
    tags: Tag[];
    selectTag: (tag: Tag) => void;
    showUpdateForm: (tag: Tag) => void;
    showDeleteForm: (tag: Tag) => void;
    showCreateForm: () => void;
    handleReorder: (event: DragEndEvent) => void;
    articlesByTag: Map<string, NoContentArticle[]>;
    roles: string[];
    privileges: string[];
}

interface SortableTagProps {
    tag: Tag;
    articlesCount: number;
    selectTag: (tag: Tag) => void;
    showUpdateForm: (tag: Tag) => void;
    showDeleteForm: (tag: Tag) => void;
    roles: string[];
    privileges: string[];
}

const SortableTag = ({
    selectTag,
    showDeleteForm,
    showUpdateForm,
    tag,
    articlesCount,
    roles,
    privileges
}: SortableTagProps) => {
    const { attributes, listeners, setNodeRef, transform, transition, setActivatorNodeRef } =
        useSortable({ id: tag.id });

    const style: CSSProperties = {
        transform: CSS.Transform.toString(transform),
        cursor: 'move',
        transition,
    };
    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
        >
            <Col>
                <Badge.Ribbon
                    text={articlesCount}
                    color="rgb(45, 38, 82)"
                    placement="start"
                >
                    <button
                        onClick={(e: React.MouseEvent) => {
                            if ((e.target as HTMLElement).closest('button') === e.currentTarget)
                                selectTag(tag);
                        }}
                        className={clsx('button-card', 'button-card__tag')}
                    >
                        <DragOutlined
                            ref={setActivatorNodeRef}
                            style={{ cursor: 'move', fontSize: '1.25rem' }}
                            {...listeners}
                        />
                        <TagIcon
                            name={tag.icon}
                            size={45}
                        />
                        {tag.ru.title}
                        <Controls
                            noLabel
                            items={getMenuItems<Tag>(
                                tag,
                                showUpdateForm,
                                showDeleteForm,
                                roles,
                                privileges,
                                'PRIV_CATEGORY_EDIT',
                                'PRIV_CATEGORY_DELETE',
                            )}
                        />
                    </button>
                </Badge.Ribbon>
            </Col>
        </div>
    );
};

const CategoryView = memo(
    ({
        tags,
        selectTag,
        showUpdateForm,
        showCreateForm,
        showDeleteForm,
        handleReorder,
        articlesByTag,
        roles,
        privileges,
    }: CategoryViewProps) => {
        const sensors = useSensors(
            useSensor(PointerSensor),
            useSensor(KeyboardSensor, {
                coordinateGetter: sortableKeyboardCoordinates,
            })
        );

        // const handleDragStart = (event) => {
        //     console.log(event);
        // };

        // const handleDragCancel = (event) => {
        //     console.log(event);
        // };

        const handleDragEnd = (event) => {
            handleReorder(event);
        };

        return (
            <Space
                size={18}
                direction="vertical"
            >
                <Row>
                    {validatePrivileges(roles, privileges, 'PRIV_RUBRIC_CREATE') && (
                        <Button
                            icon={<PlusCircleOutlined />}
                            onClick={showCreateForm}
                            ghost
                        >
                            Добавить рубрику
                        </Button>
                    )}
                </Row>
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    // onDragStart={handleDragStart}
                    // onDragCancel={handleDragCancel}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={tags.map((item) => item.id)}
                        strategy={rectSortingStrategy}
                    >
                        <Row gutter={[20, 18]}>
                            {[...articlesByTag.keys()].map((tagId) => {
                                const tag = tags.find((tag) => tag.id === tagId);
                                const articlesCount = articlesByTag.get(tagId).length;
                                if (!tag) {
                                    return null;
                                }
                                return (
                                    <SortableTag
                                        key={tag.id}
                                        tag={tag}
                                        articlesCount={articlesCount}
                                        selectTag={selectTag}
                                        showUpdateForm={showUpdateForm}
                                        showDeleteForm={showDeleteForm}
                                        roles={roles}
                                        privileges={privileges}
                                    />
                                );
                            })}
                        </Row>
                    </SortableContext>
                </DndContext>
            </Space>
        );
    }
);

export default CategoryView;
